import { bestPrices, displayOdds, meetsMinStake, pricedBelowMin, type OutcomeBest } from "@/lib/odds-screen/live/derive";
import { ladderLineId, type BookCell, type BookId, type EventHeader, type FamilyLayout, type Ladder, type MarketQuote, type MarketSpec, type OddsDisplay } from "@/lib/odds-screen/live/types";
import { cellLineLabel, outcomeDisplayOrder, outcomeName } from "./labels";

export type AllBooksCell =
  | { readonly kind: "none" }
  | { readonly kind: "main"; readonly display: string; readonly stake: number | null; readonly belowMin: boolean; readonly best: boolean; readonly held?: boolean }
  | { readonly kind: "off"; readonly off: string; readonly display: string; readonly stake: number | null; readonly belowMin: boolean; readonly held?: boolean };

export type AllBooksColumn = { readonly book: BookId; readonly cells: readonly AllBooksCell[] };

export type AllBooksRow = {
  readonly outcomes: readonly number[];
  readonly labels: readonly string[];
  readonly best: readonly (OutcomeBest | null)[];
  readonly bestBelowMin: readonly boolean[];
  readonly consensus: readonly string[] | null;
  readonly columns: readonly AllBooksColumn[];
};

export function allBooksRow(
  quote: MarketQuote,
  spec: MarketSpec,
  event: EventHeader | undefined,
  layout: FamilyLayout,
  books: readonly BookId[],
  selected: ReadonlySet<BookId>,
  format: OddsDisplay,
  minStake: number,
): AllBooksRow {
  const order = outcomeDisplayOrder(spec);
  const best = bestPrices(quote, spec, selected, format, minStake);
  const cellByBook = new Map(quote.books.map((cell) => [cell.book, cell]));
  const consensus = quote.consensus;

  const columns = books.map((book): AllBooksColumn => {
    const cell = cellByBook.get(book);
    const cells = order.map((outcome): AllBooksCell => {
      if (cell === undefined) return { kind: "none" };
      const odds = cell.odds[outcome];
      if (odds === null || odds === undefined) return { kind: "none" };
      const display = displayOdds(odds, format);
      const stake = cell.stakes?.[outcome] ?? null;
      const belowMin = !meetsMinStake(stake, minStake);
      if (cell.at === "off") return { kind: "off", off: cellLineLabel(spec, cell.line, outcome), display, stake, belowMin, held: cell.held?.[outcome] };
      if (cell.held?.[outcome]) return { kind: "main", display, stake, belowMin, best: false, held: true };
      return { kind: "main", display, stake, belowMin, best: best[outcome]?.books.includes(book) ?? false };
    });
    return { book, cells };
  });

  return {
    outcomes: order,
    labels: order.map((outcome) => (layout === "named" ? outcomeName(spec, event, outcome) : cellLineLabel(spec, quote.main, outcome))),
    best: order.map((outcome) => best[outcome]),
    bestBelowMin: order.map((outcome) => best[outcome] === null && pricedBelowMin(quote, outcome, selected, minStake)),
    consensus: consensus === undefined ? null : order.map((outcome) => displayOdds(1 / consensus[outcome], format)),
    columns,
  };
}

export function ladderQuote(ladder: Ladder, slot: number, line: number): MarketQuote | null {
  const at = ladder.lines.get(ladderLineId(slot, line));
  if (at === undefined) return null;
  return {
    main: line,
    lines: [...ladder.lines.values()].filter((candidate) => candidate.slot === slot).length,
    books: at.books.map(({ book, outcomes }): BookCell => ({
      at: "main",
      book,
      odds: outcomes.map((outcome) => outcome?.odds ?? null),
      stakes: outcomes.map((outcome) => outcome?.stake ?? null),
      held: outcomes.map((outcome) => outcome?.held === true),
    })),
  };
}

export const MAX_PINNED_ROWS = 8;

export function pinLine(picks: ReadonlyMap<string, number>, key: string, line: number | null): ReadonlyMap<string, number> {
  const next = new Map(picks);
  next.delete(key);
  if (line === null) return next;
  next.set(key, line);
  for (const oldest of next.keys()) {
    if (next.size <= MAX_PINNED_ROWS) break;
    next.delete(oldest);
  }
  return next;
}
